import { useState } from 'react'
import {
  validateCoupons as validateCouponsApi,
  getAutoApplyDiscounts as getAutoApplyDiscountsApi,
  applyCouponsToOrder as applyCouponsToOrderApi,
} from '../services/couponApi'
import {
  ValidateCouponsResponse,
  AutoApplyDiscountsResponse,
  AutoApplyDiscount,
} from '../types/coupon'

/**
 * Hook for validating coupon codes
 */
export function useValidateCoupons() {
  const [isValidating, setIsValidating] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)

  const validateCoupons = async (
    codes: string[],
    orderSubtotal: number,
    customerId?: string,
  ): Promise<ValidateCouponsResponse | null> => {
    setIsValidating(true)
    setValidationError(null)

    try {
      const response = await validateCouponsApi(
        codes,
        orderSubtotal,
        customerId,
      )
      return response
    } catch (error: any) {
      const errorMessage =
        error?.message || 'خطا در اعتبارسنجی کد تخفیف. لطفا دوباره تلاش کنید.'
      setValidationError(errorMessage)
      return null
    } finally {
      setIsValidating(false)
    }
  }

  return {
    validateCoupons,
    isValidating,
    validationError,
  }
}

/**
 * Hook for fetching auto-apply discounts
 */
export function useAutoApplyDiscounts(
  orderSubtotal: number,
  customerId?: string,
) {
  const [autoApplyDiscounts, setAutoApplyDiscounts] = useState<
    AutoApplyDiscount[]
  >([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchAutoApplyDiscounts = async () => {
    if (orderSubtotal <= 0) return

    setIsLoading(true)
    setError(null)

    try {
      const response: AutoApplyDiscountsResponse =
        await getAutoApplyDiscountsApi(orderSubtotal, customerId)

      if (response.success && response.data) {
        setAutoApplyDiscounts(response.data)
      }
    } catch (err: any) {
      console.error('Error fetching auto-apply discounts:', err)
      setError(err?.message || 'خطا در دریافت تخفیف‌های خودکار')
    } finally {
      setIsLoading(false)
    }
  }

  return {
    autoApplyDiscounts,
    isLoading,
    error,
    fetchAutoApplyDiscounts,
  }
}

/**
 * Hook for applying coupons to an order
 */
export function useApplyCoupons() {
  const [isApplying, setIsApplying] = useState(false)
  const [applyError, setApplyError] = useState<string | null>(null)

  const applyCoupons = async (orderId: string, couponCodes: string[]) => {
    setIsApplying(true)
    setApplyError(null)

    try {
      const response = await applyCouponsToOrderApi(orderId, couponCodes)

      // The API returns the order object directly
      // Check if it has the required discount properties
      if (response && response.appliedDiscounts !== undefined) {
        // Return the discount data in the format expected by the component
        return {
          appliedDiscounts: response.appliedDiscounts,
          totalDiscountAmount: response.totalDiscountAmount || 0,
          subTotal: response.subTotal,
          total: response.total,
        }
      } else {
        throw new Error('Invalid response from server')
      }
    } catch (error: any) {
      const errorMessage =
        error?.message || 'خطا در اعمال کد تخفیف. لطفا دوباره تلاش کنید.'
      setApplyError(errorMessage)
      return null
    } finally {
      setIsApplying(false)
    }
  }

  return {
    applyCoupons,
    isApplying,
    applyError,
  }
}
